<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate @submit.prevent="submit()">
    <v-row no-gutters>
      <v-card max-width="1200" flat color="transparent">
        <v-card-title>
          <FormHeader
            :loading="loading"
            :link="'page-attributes'"
            :title="data.displayName ? `${data.displayName} атрибут` : `Создать атрибут`"
          />
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-select
                  ref="typeSelect"
                  label="Тип *"
                  id="type"
                  outlined
                  item-value="type"
                  item-text="type"
                  :items="$options.attributeTypes"
                  :error-messages="validationErrors.type"
                  :disabled="loading"
                  :value="fields.type"
                  @change="updateTypeHandler"
                  @input="$v.fields.type.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <span>Заголовок * (<TranslationIcon />)</span>
                <v-text-field
                  outlined
                  autofocus
                  id="displayName"
                  :error-messages="validationErrors.displayName"
                  :disabled="loading || !fields.type"
                  v-model="fields.displayName"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  outlined
                  id="name"
                  :label="'Имя *'"
                  :disabled="loading || !fields.type"
                  v-model="fields.name"
                  :error-messages="validationErrors.name"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-divider />
              </v-col>
            </v-row>
            <v-row align="center" justify="space-between">
              <v-col cols="auto"> Правила валидации: </v-col>
              <v-col cols="auto">
                <v-menu transition="scroll-y-transition" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      :disabled="loading || !fields.type || !attributeRules().length"
                    >
                      <v-icon class="pr-1"> mdi-plus </v-icon>
                      <span>Добавить</span>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(item, itemIndex) in attributeRules()"
                      :key="itemIndex"
                      :disabled="!!validationRule(item)"
                      @click="fields.validationRules.push(Object.assign({}, item))"
                    >
                      {{ item.title }}
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
            <v-row
              v-for="(validationRule, validationRuleIndex) in fields.validationRules"
              :key="validationRuleIndex"
            >
              <v-card flat color="transparent">
                <v-card-title>
                  <v-row no-gutters align="center">
                    {{ validationRule.title }}
                    <v-btn
                      icon
                      :disabled="loading"
                      @click="
                        fields.validationRules = fields.validationRules.filter(
                          i => i.type !== validationRule.type
                        )
                      "
                    >
                      <v-icon color="red">mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-row>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="auto" v-if="validationRule.isValueFieldAvailable">
                      <v-text-field
                        label="Значение валидации"
                        outlined
                        :disabled="loading"
                        v-model="validationRule.value"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <v-text-field
                        label="Сообщение валидации"
                        outlined
                        :disabled="loading"
                        v-model="validationRule.message"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
      <v-spacer />
      <ManageSidePanel
        :isNew="isNew"
        :created-at="data.createdAt"
        :updated-at="data.updatedAt"
        :published-at="data.publishedAt"
        @delete="$emit('delete-attr')"
        @cancel="$router.push({ name: 'page-attributes' })"
        :language="language"
        :translations="fields.translations"
        :entity-id="data.id"
        :entity-type="'attribute'"
        @update-translations="Object.assign(fields.translations, $event)"
        @language-update="$emit('language-update', $event)"
      /> </v-row
    >>
    <ConfirmationDialog
      ref="confirmTypeChanging"
      confirmText="Подвтердить"
      description="Изменение 'Типа' приведет к обнулению данных валидации"
    />
  </form>
</template>
<script>
import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import validationRules from './validation-rules'
import attributeTypes from '@/configs/attributeTypes'
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import FormHeader from '@/components/form-header'
import ProgressCircular from '@/components/progress-circular'

export default {
  name: 'FormAttribute',
  components: {
    TranslationIcon,
    ConfirmationDialog,
    ManageSidePanel,
    FormHeader,
    ProgressCircular
  },
  attributeTypes,
  validationRules,
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations],
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fields: {
        type: null,
        displayName: null,
        name: null,
        validationRules: [],
        translations: { ru: { isEnabled: true } }
      }
    }
  },
  watch: {
    data (val) {
      this.populate(val)
    }
  },
  methods: {
    resetAttr () {
      this.fields.name = null
      this.fields.displayName = null
      this.fields.validationRules = []
    },
    validationRule (item) {
      if (!this.fields.validationRule) return null
      return this.fields.validationRules.find(i => i.type === item.type)
    },
    async updateTypeHandler (value) {
      if (value !== this.fields.type) {
        this.resetAttr()
      }
      if (this.fields.validationRules.length) {
        try {
          await this.$refs.confirmTypeChanging.open()
          this.fields.validationRules = []
          this.fields.type = value
        } catch (e) {
          this.$refs.typeSelect.setValue(this.fields.type)
        } finally {
          this.$refs.confirmTypeChanging.close()
        }
      } else {
        this.fields.type = value
      }
    },
    attributeRules () {
      const attributeTypes = this.$options.attributeTypes
      const foundType =
        attributeTypes.find(attributeType => attributeType.type === this.fields.type) ??
        {}
      if (foundType.availableValidations) return foundType.availableValidations
      return []
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.mixPrepareTranslations(payload)

      this.$emit('submit', payload)
    }
  }
}
</script>
